import { useRef, useState } from "react";
import { trpc } from "../../_core/trpc";
import { Panel } from "./Admin";
import { LIMITS, useUploader } from "./upload";
import { UploadStatusBar } from "./UploadStatus";
function AdminPhotos() {
  const list = trpc.admin.listMedia.useQuery({ kind: "photo" });
  const update = trpc.admin.updateMedia.useMutation();
  const remove = trpc.admin.deleteMedia.useMutation();
  const reorder = trpc.admin.reorderMedia.useMutation();
  const settings = trpc.admin.getSettings.useQuery();
  const saveSettings = trpc.admin.saveSettings.useMutation();
  const utils = trpc.useUtils();
  const { uploadOne, status, reset, busy } = useUploader("photo");
  const fileRef = useRef(null);
  const [queued, setQueued] = useState(0);
  const [doneCount, setDoneCount] = useState(0);
  const [errors, setErrors] = useState([]);
  const [confirmId, setConfirmId] = useState(null);
  const photos = list.data ?? [];
  const refresh = async () => {
    await utils.admin.listMedia.invalidate();
    await utils.admin.overview.invalidate();
    await utils.public.state.invalidate();
  };
  const onFiles = async (files) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);
    setErrors([]);
    setQueued(arr.length);
    setDoneCount(0);
    for (const f of arr) {
      const res = await uploadOne(f);
      if (!res.ok && res.message) setErrors((e) => [...e, `${f.name}: ${res.message}`]);
      setDoneCount((c) => c + 1);
    }
    await refresh();
    setQueued(0);
    if (fileRef.current) fileRef.current.value = "";
    reset();
  };
  const move = async (index, dir) => {
    const next = [...photos];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    await reorder.mutateAsync({ ids: next.map((p) => p.id) });
    await refresh();
  };
  const dragFrom = useRef(null);
  const [overIndex, setOverIndex] = useState(null);
  const [localOrder, setLocalOrder] = useState(null);
  const shown = localOrder ?? photos;
  const onPointerDown = (e, index) => {
    if (e.button !== 0 && e.pointerType === "mouse") return;
    dragFrom.current = index;
    setOverIndex(index);
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (dragFrom.current === null) return;
    const el = document.elementFromPoint(e.clientX, e.clientY);
    const card = el?.closest("[data-photo-index]");
    if (!card) return;
    const idx = Number(card.dataset.photoIndex);
    if (!Number.isFinite(idx)) return;
    setOverIndex(idx);
    if (idx === dragFrom.current) return;
    const from = dragFrom.current;
    const next = [...shown];
    const [moved] = next.splice(from, 1);
    next.splice(idx, 0, moved);
    dragFrom.current = idx;
    setLocalOrder(next);
  };
  const onPointerUp = async () => {
    if (dragFrom.current === null) return;
    const order = localOrder;
    dragFrom.current = null;
    setOverIndex(null);
    if (!order) return;
    const ids = order.map((p) => p.id);
    const changed = ids.some((id, i) => id !== photos[i]?.id);
    if (!changed) {
      setLocalOrder(null);
      return;
    }
    try {
      await reorder.mutateAsync({ ids });
      await refresh();
    } finally {
      setLocalOrder(null);
    }
  };
  return <Panel
    eyebrow="Photos"
    title="The gallery"
    description={`Upload several at once. ${LIMITS.photo.label}. Large photos are resized in your browser before upload, so they load quickly on her phone.`}
    aside={<div className="flex items-center gap-2">
          <span className="pill">{photos.length} photo{photos.length === 1 ? "" : "s"}</span>
        </div>}
  >
      {
    /* ── uploader ── */
  }
      <div
    className="glass rounded-xl p-5"
    onDragOver={(e) => {
      e.preventDefault();
      e.currentTarget.dataset.drag = "1";
    }}
    onDragLeave={(e) => {
      delete e.currentTarget.dataset.drag;
    }}
    onDrop={(e) => {
      e.preventDefault();
      delete e.currentTarget.dataset.drag;
      void onFiles(e.dataTransfer.files);
    }}
    data-drag=""
  >
        <div className="flex flex-col items-center gap-3 py-4 text-center">
          <div className="grid h-12 w-12 place-items-center rounded-full border border-[rgba(233,168,242,.3)]">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
    d="M12 16V4m0 0L7 9m5-5 5 5M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"
    stroke="#e9a8f2"
    strokeWidth="1.6"
    strokeLinecap="round"
  />
            </svg>
          </div>
          <div>
            <p className="font-display text-lg text-[#f3f4f9]">Drop photos here, or choose files</p>
            <p className="mt-1 text-[0.72rem] text-[color:var(--muted-foreground)]">
              {LIMITS.photo.label} · multiple selection supported
            </p>
          </div>
          <button
    type="button"
    onClick={() => fileRef.current?.click()}
    className="btn-gold !min-h-[42px] !text-[0.66rem]"
    disabled={busy}
  >
            {busy ? `Uploading ${doneCount + 1} of ${queued}\u2026` : "Choose photos"}
          </button>
          <input
    ref={fileRef}
    type="file"
    accept={LIMITS.photo.types.join(",")}
    multiple
    className="hidden"
    onChange={(e) => void onFiles(e.target.files)}
  />
        </div>

        <div className="space-y-2">
          <UploadStatusBar status={status} />
          {errors.length > 0 && <p role="alert" className="mt-2 text-[0.72rem] text-[#ffc9cd]">
              {errors.length} upload{errors.length === 1 ? "" : "s"} did not finish. The message above
              says why — nothing was lost, you can choose the same files again.
            </p>}
          <p className="text-[0.7rem] text-[color:var(--muted-foreground)]">
            Drag the handle on a photo to reorder, or use the arrows.
          </p>
          {queued > 0 && <p className="text-[0.72rem] text-[color:var(--muted-foreground)]">
              {doneCount} of {queued} finished
            </p>}
          {errors.map((e, i) => <p key={i} role="alert" className="text-[0.75rem] text-[#ffc9cd]">
              {e}
            </p>)}
        </div>
      </div>

      {
    /* ── gallery layout ── */
  }
      <div className="glass mt-4 rounded-xl p-5">
        <p className="eyebrow mb-3">Gallery layout</p>
        <div className="flex flex-wrap gap-2">
          {[
    ["masonry", "Masonry \u2014 mixed heights"],
    ["grid", "Grid \u2014 even tiles"],
    ["carousel", "Carousel \u2014 swipe through"]
  ].map(([value, label]) => {
    const active = settings.data?.galleryLayout === value;
    return <button
      key={value}
      type="button"
      onClick={async () => {
        await saveSettings.mutateAsync({ galleryLayout: value });
        await utils.admin.getSettings.invalidate();
        await utils.public.state.invalidate();
      }}
      className={`rounded-full px-3.5 py-2 text-[0.68rem] uppercase tracking-[0.12em] transition ${active ? "bg-[rgba(233,168,242,.18)] text-[#e9a8f2] ring-1 ring-[rgba(233,168,242,.45)]" : "border border-[rgba(233,168,242,.18)] text-[color:var(--muted-foreground)] hover:text-[#dcdff0]"}`}
    >
                {label}
              </button>;
  })}
        </div>
      </div>

      {
    /* ── list ── */
  }
      <div className="mt-5">
        {list.isLoading && <p className="text-sm text-[color:var(--muted-foreground)]">Loading photos…</p>}

        {!list.isLoading && photos.length === 0 && <div className="glass rounded-xl px-6 py-14 text-center">
            <p className="font-display text-lg text-[#f3f4f9]">No photos yet</p>
            <p className="mx-auto mt-2 max-w-md text-sm text-[color:var(--muted-foreground)]">
              Add a few above and they will fill the gallery on her page. Use the arrows to set the
              order she sees them in.
            </p>
          </div>}

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {shown.map((p, i) => <div
    key={p.id}
    data-photo-index={i}
    data-dragging={dragFrom.current === i ? "true" : void 0}
    data-over={overIndex === i && dragFrom.current !== i ? "true" : void 0}
    className="drag-item glass overflow-hidden rounded-xl"
    onPointerMove={onPointerMove}
    onPointerUp={() => void onPointerUp()}
    onPointerCancel={() => void onPointerUp()}
  >
              <div className="relative aspect-[4/3] bg-black/40">
                <img
    src={p.url}
    alt={p.caption ?? `Photo ${i + 1}`}
    loading="lazy"
    className="h-full w-full object-cover"
  />
                <button
    type="button"
    onPointerDown={(e) => onPointerDown(e, i)}
    aria-label={`Reorder photo ${i + 1} \u2014 drag, or use the arrow buttons`}
    title="Drag to reorder"
    className="drag-handle absolute left-1.5 top-1.5 grid h-9 w-9 place-items-center rounded-lg bg-black/65 text-[#e9a8f2] backdrop-blur transition hover:bg-black/85"
  >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <circle cx="9" cy="6" r="1.5" />
                    <circle cx="15" cy="6" r="1.5" />
                    <circle cx="9" cy="12" r="1.5" />
                    <circle cx="15" cy="12" r="1.5" />
                    <circle cx="9" cy="18" r="1.5" />
                    <circle cx="15" cy="18" r="1.5" />
                  </svg>
                </button>
                <span className="pointer-events-none absolute right-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-[0.62rem] tabular-nums text-[#e9a8f2]">
                  {i + 1}
                </span>
                {p.visibleBeforeUnlock && <span className="pill pill-locked pointer-events-none absolute bottom-2 left-2 !bg-black/60">
                    teaser
                  </span>}
              </div>

              <div className="space-y-2.5 p-3">
                <input
    className="field-lux !min-h-[36px] !text-[0.78rem]"
    defaultValue={p.caption ?? ""}
    placeholder="Caption (optional)"
    aria-label={`Caption for photo ${i + 1}`}
    onBlur={async (e) => {
      const v = e.target.value.trim();
      if (v !== (p.caption ?? "")) {
        await update.mutateAsync({ id: p.id, caption: v || null });
        await refresh();
      }
    }}
  />

                <div className="flex items-center gap-1.5">
                  <button
    type="button"
    onClick={() => void move(i, -1)}
    disabled={i === 0}
    className="grid h-11 w-11 place-items-center rounded-lg border border-[rgba(233,168,242,.2)] text-[#a9aecb] disabled:opacity-30"
    aria-label={`Move photo ${i + 1} earlier`}
  >
                    ←
                  </button>
                  <button
    type="button"
    onClick={() => void move(i, 1)}
    disabled={i === photos.length - 1}
    className="grid h-11 w-11 place-items-center rounded-lg border border-[rgba(233,168,242,.2)] text-[#a9aecb] disabled:opacity-30"
    aria-label={`Move photo ${i + 1} later`}
  >
                    →
                  </button>

                  <button
    type="button"
    onClick={async () => {
      await update.mutateAsync({ id: p.id, visibleBeforeUnlock: !p.visibleBeforeUnlock });
      await refresh();
    }}
    className="ml-1 min-h-[44px] rounded-lg border border-[rgba(233,168,242,.2)] px-2 py-1.5 text-[0.6rem] uppercase tracking-[0.1em] text-[#a9aecb] hover:text-[#e9a8f2]"
    aria-pressed={p.visibleBeforeUnlock}
  >
                    {p.visibleBeforeUnlock ? "Teaser on" : "Teaser off"}
                  </button>

                  <div className="ml-auto">
                    {confirmId === p.id ? <div className="flex gap-1">
                        <button
    type="button"
    onClick={async () => {
      await remove.mutateAsync({ id: p.id });
      setConfirmId(null);
      await refresh();
    }}
    className="rounded-md bg-[#e11d48] px-2 py-1.5 text-[0.6rem] uppercase tracking-wider text-white"
  >
                          Delete
                        </button>
                        <button
    type="button"
    onClick={() => setConfirmId(null)}
    className="rounded-md border border-white/15 px-2 py-1.5 text-[0.6rem] uppercase tracking-wider text-[color:var(--muted-foreground)]"
  >
                          No
                        </button>
                      </div> : <button
    type="button"
    onClick={() => setConfirmId(p.id)}
    className="rounded-md px-2 py-1.5 text-[0.6rem] uppercase tracking-wider text-[color:var(--muted-foreground)] hover:text-[#ffc9cd]"
  >
                        Delete
                      </button>}
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </Panel>;
}
export {
  AdminPhotos as default
};
